import React, { useMemo, useState } from "react";
import { Archive, Copy, Eye, KeyRound, MoreVertical, Search, Send } from "lucide-react";
import PreviewModal from "./PreviewModal";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function ApiKeyModal({ open, form, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!open || !form) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(form.apiKey || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-left text-lg font-semibold text-[#1D2739]">API Key</h3>
        <p className="mt-1 text-left text-sm text-gray-500">{form.name}</p>
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
          <span className="flex-1 break-all text-left text-sm text-gray-800">
            {form.apiKey}
          </span>
          <button
            type="button"
            onClick={copy}
            className="rounded p-1 text-gray-500 hover:bg-white"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
        {copied && (
          <p className="mt-2 text-left text-xs font-medium text-green-600">Copied</p>
        )}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-[#EAF524] px-5 py-2 text-sm font-semibold text-black"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SendToSuppliersModal({ open, form, onClose, onSend }) {
  const [emails, setEmails] = useState("");
  if (!open || !form) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-left text-lg font-semibold text-[#1D2739]">
          Send to Suppliers
        </h3>
        <p className="mt-1 text-left text-sm text-gray-500">
          Share {form.name} with supplier email addresses
        </p>
        <textarea
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="Enter emails, one per line"
          rows={5}
          className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-100"
        />
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSend(emails);
              setEmails("");
            }}
            className="rounded-lg bg-[#EAF524] px-4 py-2 text-sm font-semibold text-black"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CompletedFormsList({ forms, onArchive, onToast }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("active");
  const [menuId, setMenuId] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [apiKeyForm, setApiKeyForm] = useState(null);
  const [sendForm, setSendForm] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = forms.filter((f) =>
      statusFilter === "archived"
        ? f.publishStatus === "archived"
        : f.publishStatus !== "archived"
    );
    if (q) {
      list = list.filter((f) => f.name.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      const da = new Date(a.updatedAt).getTime();
      const db = new Date(b.updatedAt).getTime();
      if (sort === "oldest") return da - db;
      if (sort === "name") return a.name.localeCompare(b.name);
      return db - da;
    });
    return list;
  }, [forms, query, sort, statusFilter]);

  const copyLink = async (form) => {
    try {
      await navigator.clipboard.writeText(form.publicUrl || "");
      onToast?.({ title: "Copied", message: "Share link copied to clipboard." });
    } catch {
      onToast?.({ title: "Copied", message: "Share link copied to clipboard." });
    }
    setMenuId(null);
  };

  return (
    <div className="px-6 py-6 text-left">
      <h2 className="mb-6 text-xl font-semibold text-[#1D2739]">Completed Forms</h2>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Forms by Name"
            className="w-[320px] rounded-lg bg-gray-100 py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <button
          type="button"
          className="rounded-lg bg-blue-500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-600"
        >
          Search
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="name">Name A–Z</option>
        </select>
        <div className="ml-auto flex rounded-lg border border-gray-200 p-0.5">
          {["active", "archived"].map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setStatusFilter(id)}
              className={`rounded-md px-3 py-1.5 text-sm capitalize ${
                statusFilter === id
                  ? "bg-gray-100 font-medium text-[#1D2739]"
                  : "text-gray-500"
              }`}
            >
              {id}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500">
          {statusFilter === "archived"
            ? "No archived forms."
            : "No published forms yet. Publish a draft to see it here."}
        </p>
      ) : (
        <div className="overflow-visible rounded-xl border border-gray-100">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-white text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Form Name</th>
                <th className="px-4 py-3 font-medium">Created By</th>
                <th className="px-4 py-3 font-medium">Date Created</th>
                <th className="px-4 py-3 font-medium">Last Modified</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((form) => (
                <tr
                  key={form.id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60"
                >
                  <td className="px-4 py-4 font-medium text-[#1D2739]">{form.name}</td>
                  <td className="px-4 py-4 text-gray-600">{form.createdBy}</td>
                  <td className="px-4 py-4 text-gray-600">{formatDate(form.createdAt)}</td>
                  <td className="px-4 py-4 text-gray-600">{formatDate(form.updatedAt)}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        form.publishStatus === "archived"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-green-50 text-green-700"
                      }`}
                    >
                      {form.publishStatus === "archived" ? "Archived" : "Active"}
                    </span>
                  </td>
                  <td className="relative px-4 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => setMenuId(menuId === form.id ? null : form.id)}
                      className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {menuId === form.id && (
                      <div className="absolute right-4 z-20 mt-1 w-52 rounded-lg border border-gray-100 bg-white py-1 text-left shadow-lg">
                        <button
                          type="button"
                          onClick={() => copyLink(form)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Copy className="h-4 w-4" /> Copy Share Link
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            setApiKeyForm(form);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <KeyRound className="h-4 w-4" /> View API Key
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            setPreviewForm(form);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Eye className="h-4 w-4" /> Preview Form
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            setSendForm(form);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Send className="h-4 w-4" /> Send to Suppliers
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            onArchive(form.id);
                            onToast?.(
                              form.publishStatus === "archived"
                                ? {
                                    title: "Form activated",
                                    message: `${form.name} is now active.`,
                                  }
                                : {
                                    title: "Form archived",
                                    message: `${form.name} has been archived.`,
                                  }
                            );
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Archive className="h-4 w-4" />
                          {form.publishStatus === "archived"
                            ? "Activate Form"
                            : "Archive Form"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PreviewModal
        open={!!previewForm}
        form={previewForm}
        onClose={() => setPreviewForm(null)}
      />
      <ApiKeyModal
        open={!!apiKeyForm}
        form={apiKeyForm}
        onClose={() => setApiKeyForm(null)}
      />
      <SendToSuppliersModal
        open={!!sendForm}
        form={sendForm}
        onClose={() => setSendForm(null)}
        onSend={() => {
          setSendForm(null);
          onToast?.({
            title: "Sent",
            message: "Form sent to suppliers.",
          });
        }}
      />
    </div>
  );
}
